/**
 * This file re-exports core TanStack Table types to isolate table migrations.
 *
 * See https://github.com/github/memex/issues/20319 for more details.
 */
import type {
  Cell as TanStackCell,
  Column as TanStackColumn,
  ColumnDef as TanStackColumnDef,
  Header as TanStackHeader,
  HeaderGroup as TanStackHeaderGroup,
  ReactTable as TanStackReactTable,
  Row as TanStackRow,
  RowData,
  RowModel as TanStackRowModel,
  StockFeatures,
  Table as TanStackTable,
  TableFeature,
  TableFeatures,
  TableState as TanStackTableState,
} from '@tanstack/react-table'

import type {MissingSubIssue} from '../../../api/common-contracts'
import type {AgentTask} from '../../../api/memex-items/contracts'
import type {Group} from '../../../api/memex-items/paginated-views'
import type {FieldGrouping} from '../../../features/grouping/types'
import type {usePaginatedMemexItemsQueryReturnType} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'

type QueryDataTableState = Pick<
  usePaginatedMemexItemsQueryReturnType,
  'groupedItemQueries' | 'queriesForGroups' | 'groupsById'
> & {
  subIssuesQueryData: Pick<usePaginatedMemexItemsQueryReturnType, 'subIssuesByParentId'> & {
    copilotTasksEnabled: boolean
  }
}

type MemexTableState = {
  groupQueryData: Pick<QueryDataTableState, 'groupedItemQueries' | 'queriesForGroups' | 'groupsById'>
  subIssuesQueryData: QueryDataTableState['subIssuesQueryData']
}

export type CustomGroupingRow = {
  groupQueryData: Omit<Group, 'fieldMetrics'>
  /** @deprecated Use groupingValue instead. */
  groupedValue: string
  /** @deprecated Use groupQueryData.groupMetadata instead. */
  groupedSourceObject: FieldGrouping
  /** @deprecated Use groupQueryData.totalCount instead. */
  totalCount: number
  subIssueData?: {
    missingSubIssues?: Array<MissingSubIssue>
    subIssueDepth?: number
  }
  /** When set, this row represents a virtual agent assignment task rather than a MemexProjectItem. */
  agentAssignmentTaskData?: {
    task: AgentTask
  }
}

/**
 * @link [Declaration merging](https://tanstack.com/table/latest/docs/guide/custom-features#step-2-use-declaration-merging-to-add-new-types-to-tanstack-table)
 */
declare module '@tanstack/react-table' {
  interface Plugins {
    memexTableFeature: TableFeature
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface TableState_FeatureMap {
    memexTableFeature: MemexTableState
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, unused-imports/no-unused-vars -- Must match TanStack's declaration.
  interface Row_FeatureMap<TFeatures extends TableFeatures, TData extends RowData> {
    memexTableFeature: CustomGroupingRow
  }
}

export const memexTableFeature: TableFeature = {}

export type MemexTableFeatures = StockFeatures & {memexTableFeature: TableFeature}
export type Cell<TData extends object> = TanStackCell<MemexTableFeatures, TData, unknown>
export type Column<TData extends object> = TanStackColumn<MemexTableFeatures, TData, unknown>
export type ColumnDef<TData extends object> = TanStackColumnDef<MemexTableFeatures, TData, unknown> & {id: string}
export type ColumnInstance<TData extends object> = TanStackColumn<MemexTableFeatures, TData, unknown>
export type HeaderGroup<TData extends object> = TanStackHeaderGroup<MemexTableFeatures, TData>
export type Header<TData extends object> = TanStackHeader<MemexTableFeatures, TData, unknown>
export type Row<TData extends object> = TanStackRow<MemexTableFeatures, TData>
export type RowModel<TData extends object> = TanStackRowModel<MemexTableFeatures, TData>
export type CoreTable<TData extends object> = TanStackTable<MemexTableFeatures, TData>
export type TableState = TanStackTableState<MemexTableFeatures>
export type Table<TData extends object> = TanStackReactTable<MemexTableFeatures, TData, TableState> & {
  getState: () => TableState
}
